import { useEffect, useState, type FormEvent, type WheelEvent } from 'react';
import { createInsumo, getProveedores, updateInsumo } from '../../lib';
import type { Insumo, Proveedor, UpsertInsumoInput } from '../../lib/appTypes';

interface Props {
  initialInsumo?: Insumo | null;
  onSaved: (insumo: Insumo) => void;
  onCancel: () => void;
}

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

export default function FormInsumo({ initialInsumo, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    nombre: initialInsumo?.nombre ?? '',
    marca: initialInsumo?.marca ?? '',
    modeloInsumo: initialInsumo?.modeloInsumo ?? '',
    precio: initialInsumo?.precio ?? '',
    stockDisponible: initialInsumo?.stockDisponible ?? '',
    proveedor: initialInsumo?.proveedor ?? '',
  });

  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(initialInsumo?.id);
  const proveedorSeleccionado = proveedores.find(proveedor => proveedor.id === form.proveedor);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoadingProveedores(true);
        const response = await getProveedores({ page_size: 500, order_by: 'nombre_fiscal', order_up: true });
        if (!cancelled) {
          setProveedores(response);
        }
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar los proveedores.');
        }
      } finally {
        if (!cancelled) {
          setLoadingProveedores(false);
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  function handle(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    if (!form.nombre.trim()) {
      setError('Ingresa el nombre del insumo.');
      return;
    }

    if (!form.proveedor) {
      setError('Selecciona un proveedor.');
      return;
    }

    const precio = Number(form.precio);
    if (!Number.isFinite(precio) || precio < 0) {
      setError('Ingresa un precio válido.');
      return;
    }

    const stockDisponible = Number(form.stockDisponible);
    if (!Number.isFinite(stockDisponible) || stockDisponible < 0) {
      setError('Ingresa un stock disponible válido.');
      return;
    }

    const payload: UpsertInsumoInput = {
      nombre: form.nombre.trim(),
      marca: form.marca.trim() || undefined,
      modeloInsumo: form.modeloInsumo.trim() || undefined,
      precio,
      stockDisponible,
      proveedor: form.proveedor,
    };

    try {
      setLoading(true);
      const insumo = initialInsumo
        ? await updateInsumo(initialInsumo.id, payload)
        : await createInsumo(payload);

      onSaved(insumo);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo guardar el insumo.');
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">
          {isEditing ? 'Editar insumo' : 'Crear insumo'}
        </h3>
        <p className="text-sm text-stone-500">
          Registra el nombre, precio, stock disponible y proveedor asociado al insumo.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={labelCls}>Nombre del insumo</label>
          <input
            name="nombre"
            value={form.nombre}
            onChange={handle}
            required
            className={inputCls}
            placeholder="Aceite, filtro, llanta..."
          />
        </div>

        <div>
          <label className={labelCls}>Marca (opcional)</label>
          <input
            name="marca"
            value={form.marca}
            onChange={handle}
            className={inputCls}
            placeholder="Ej: Castrol, Bosch..."
          />
        </div>

        <div>
          <label className={labelCls}>Modelo (opcional)</label>
          <input
            name="modeloInsumo"
            value={form.modeloInsumo}
            onChange={handle}
            className={inputCls}
            placeholder="Ej: SAE 15W-40, Premium..."
          />
        </div>

        <div>
          <label className={labelCls}>Precio</label>
          <input
            name="precio"
            type="number"
            min="0"
            step="0.01"
            value={form.precio}
            onWheel={preventNumberInputWheelChange}
            onChange={handle}
            required
            className={inputCls}
            placeholder="0.00"
          />
        </div>

        <div>
          <label className={labelCls}>Stock disponible</label>
          <input
            name="stockDisponible"
            type="number"
            min="0"
            step="0.01"
            value={form.stockDisponible}
            onWheel={preventNumberInputWheelChange}
            onChange={handle}
            required
            className={inputCls}
            placeholder="0.00"
          />
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Proveedor</label>
          <select
            name="proveedor"
            value={form.proveedor}
            onChange={handle}
            required
            disabled={loadingProveedores}
            className={inputCls}
          >
            <option value="">
              {loadingProveedores ? 'Cargando proveedores...' : 'Selecciona un proveedor'}
            </option>
            {proveedores.map(proveedor => (
              <option key={proveedor.id} value={proveedor.id}>
                {proveedor.nombreFiscal} · {proveedor.identificacion}
              </option>
            ))}
          </select>
          {proveedorSeleccionado && (
            <p className="mt-2 text-xs text-stone-500">
              Tipo: {proveedorSeleccionado.esPropia ? 'Propio' : 'Externo'}.
              {proveedorSeleccionado.esPropia
                ? ' Los insumos de este proveedor no descuentan stock al asignarse.'
                : ' Los insumos de este proveedor descuentan stock al asignarse.'}
            </p>
          )}
        </div>
      </div>

      {proveedores.length === 0 && !loadingProveedores && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No hay proveedores registrados. Primero crea un proveedor.
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || proveedores.length === 0}
          className="ui-btn ui-btn-primary"
        >
          {loading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear insumo'}
        </button>
      </div>
    </form>
  );
}
